import React, { useRef, useEffect, useCallback, useState } from "react";
import { useBoolean } from "customHooks/boolean";
import useSetState from "customHooks/setState";
import Icon from "components/Icon";
import { AudioHitLayer, PortalAudioControlsButton } from "./AudioHitLayer";
import VocabularyInfo from "./VocabularyInfo";
import { useStore, StoreTypes } from "context";
import { groupByArrayToMap, convertArrayToMap } from "util/array";
import { tabType } from "constants/vocabularyExample";
import TranslationControlsButton from "components/common/TranslationControlsButton";
import classnames from "classnames";
import styles from "./index.module.scss";
import Repository from "repositories/Repository";
import { ScrollPositionButtonStateType } from "constants/scrollPositionType";
import * as types from "constants/actionTypes";
import { ExtendedContentType } from "components/ExtendedContent/ExtendedContentContainer";
import { SplitType } from "constants/splitType";
import { EventBus, EventBusType } from "events/EventBus";
import { CanvasEvent } from "events/EventTypes";

const { ExtendedResourceRepository } = Repository;

const COUNT = 4;

const defaultContent = {
  word: "",
  wordSound: "",
  wordTranslation: "",
  KK: "",
  example: "",
  exampleSound: "",
  exampleTranslation: "",
  image: "",
  plus: [
    {
      plusContent: "",
      antonym: "",
      synonym: "",
      content: "",
      example: "",
      exampleSound: "",
      exampleTranslation: "",
      extra: {
        content: "",
        example: [],
        exampleSound: "",
        exampleTranslation: [],
      },
    },
  ],
  extra: [
    {
      content: "",
      example: "",
      exampleSound: "",
      exampleTranslation: "",
    },
  ],
};

const defaultTabContent = {
  [tabType.vocabulary]: { value: tabType.vocabulary, label: "單字" },
  [tabType.example]: { value: tabType.example, label: "例句" },
  [tabType.plus]: { value: tabType.plus, label: "補充" },
  [tabType.extra]: { value: tabType.extra, label: "一字多義" },
};

const ParagraphComponent = ({data}) => (
  <div className={styles.content}>
    {Array.isArray(data) ? (
      data.map((item) => (
        <p key={item} dangerouslySetInnerHTML={{ __html: item }} />
      ))
    ) : (
      <p dangerouslySetInnerHTML={{ __html: data }} />
    )}
  </div>
);

const PlusExtraExampleComponent = ({ audioSrc, data }) => (
  <div className={styles.example}>
    <PortalAudioControlsButton className={styles.exampleSound} src={audioSrc} />
    <span className={styles.exampleIcon}>例</span>
    <div className={styles.content}>
      {Array.isArray(data) ? (
        data.map((item) => (
          <p key={item} dangerouslySetInnerHTML={{ __html: item }} />
        ))
      ) : (
        <p dangerouslySetInnerHTML={{ __html: data }} />
      )}
    </div>
  </div>
);

const PlusExtraExampleTranslationComponent = ({ data }) => (
  <div className={styles.exampleTranslation}>
    {Array.isArray(data) ? (
      data.map((item) => (
        <p key={item} dangerouslySetInnerHTML={{ __html: item }} />
      ))
    ) : (
      <p dangerouslySetInnerHTML={{ __html: data }} />
    )}
  </div>
);

const PlusExtraComponent = ({
  bookId,
  data: {
    extraContent,
    plusContent,
    antonym,
    synonym,
    content,
    example,
    exampleSound,
    exampleTranslation,

    explanationContent,//說明
    idiomContent,//片
    usageContent,//用法
    noteContent,//備註
    sentenceContent,//句型
    compareContent,//比較
    confuseContent,//易混淆
    sentenceBlueContent//句型藍色
  },
}) => (
  <div className={styles.plusContent}>
    {content && (
      <div>
        <span dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    )}
    {plusContent && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon2, styles.idiom)}>補</span>
        <ParagraphComponent data={plusContent}/>
      </div>
    )}
    {extraContent && (
      <div className={styles.example}>
        <span
          className={classnames(styles.contentIcon3, styles.multiplyMeaning)}
        >
          一字多義
        </span>
        <ParagraphComponent data={extraContent}/>
      </div>
    )}
    {antonym && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon2, styles.idiom)}>反</span>
        <span dangerouslySetInnerHTML={{ __html: antonym }} />
      </div>
    )}
    {synonym && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon2, styles.idiom)}>同</span>
        <span dangerouslySetInnerHTML={{ __html: synonym }} />
      </div>
    )}
    {explanationContent && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon2, styles.explanation)}>說明</span>
        <ParagraphComponent data={explanationContent}/>
      </div>
    )}
    {idiomContent && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon2, styles.idiom)}>片</span>
        <ParagraphComponent data={idiomContent}/>
      </div>
    )}
    {usageContent && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon2, styles.usage)}>用法</span>
        <ParagraphComponent data={usageContent}/>
      </div>
    )}
    {noteContent && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon2, styles.note)}>備註</span>
        <ParagraphComponent data={noteContent}/>
      </div>
    )}
    {sentenceContent && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon2, styles.sentence)}>句型</span>
        <ParagraphComponent data={sentenceContent}/>
      </div>
    )}
    {compareContent && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon2, styles.compare)}>比較</span>
        <ParagraphComponent data={compareContent}/>
      </div>
    )}
    {confuseContent && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon3, styles.confuse)}>易混淆</span>
        <ParagraphComponent data={confuseContent}/>
      </div>
    )}
    {sentenceBlueContent && (
      <div className={styles.example}>
        <span className={classnames(styles.contentIcon2, styles.sentenceBlue)}>句型</span>
        <ParagraphComponent data={sentenceBlueContent}/>
      </div>
    )}
    {example && (
      <PlusExtraExampleComponent
        audioSrc={ExtendedResourceRepository.getDataSrc({
          bookId,
          pathName: exampleSound,
        })}
        data={example}
      />
    )}
    {exampleTranslation && (
      <PlusExtraExampleTranslationComponent data={exampleTranslation} />
    )}
    {/* {
        extra && (
          <PlusExtraMultiplyMeaningComponent 
            audioSrc={ExtendedResourceRepository.getDataSrc({
              bookId,
              pathName: extra.exampleSound
            })}
            data={extra}
          />
        )
      } */}
  </div>
);

const VocabularyExample = ({ data: { contents = [] }, defaultWord }) => {
  const [
    {
      currentVoca,
      vocaCurrentPage,
      vocaDataMap,
      vocaPageMap,
      vocaTotalPage,
      tabDataArr,
      currentTabValue,
      currentTabIndex,
    },
    setState,
  ] = useSetState({
    vocaCurrentPage: 1,
    vocaPageMap: null,
    vocaTotalPage: 0,
    currentVoca: defaultWord || contents[0].word,
    tabDataArr: [
      defaultTabContent[tabType.vocabulary],
      defaultTabContent[tabType.example],
    ],
    currentTabValue: tabType.vocabulary,
    currentTabIndex: 0,
  });
  const exampleRef = useRef();
  const vocabularyRef = useRef();
  const plusRef = useRef();

  const [{ bookId }] = useStore(StoreTypes.books);
  const tabsRef = useRef();

  const [isShowTW, { toggle: toggleShowTW, setFalse: hideTW }] = useBoolean();
  const [{ fontSize, currentPage, totalPages, shouldSplited, splitType }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);
  const [contentPages, setContentPages] = useState([]);
  const contentItemRefs = useRef([]);
  // 每次版面重新適配後遞增，驅動音檔按鈕的點擊代理重新對位
  const [layoutVersion, setLayoutVersion] = useState(0);

  const content = Object.assign(
    {},
    defaultContent,
    vocaDataMap ? vocaDataMap[currentVoca] : {}
  );

  // 根據 shouldSplited 和 splitType 決定 plus/extra tab 是否啟用分頁
  useEffect(() => {
    // 單字頁／例句頁固定一頁：必須明確設為 1，否則會沿用前一個頁籤（補充／一字多義）算出的頁數，
    // 讓工具列頁碼顯示成錯誤的值（例如在單字頁顯示 1/3）。
    const isSinglePageTab =
      currentTabValue === tabType.vocabulary || currentTabValue === tabType.example;

    if (isSinglePageTab || !shouldSplited || splitType === SplitType.NO_SPLIT) {
      // 不分頁：清除分頁資料，讓所有 item 全部顯示
      setContentPages([]);
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages: 1 },
      });
      return;
    }
  }, [currentTabValue, shouldSplited, splitType, extendedContentDispatch]);

  //測試新的類型用
  // content.plus = content.plus = [
  //   ...content.plus, ...[
  //     {
  //       plusContent: [
  //         "at once 同時、立刻1",
  //         "at once 同時、立刻2",
  //       ],
  //       example: [
  //         "I can’t do many things at once.",
  //         "I can’t do many things at once."
  //       ],
  //       exampleSound: "../../media/f36c5dc48135661a/audio/L1st_Tuesday.mp3",
  //       exampleTranslation: "我無法同時做很多事情。"
  //     },
  //     {//說明
  //       explanationContent: "Dear Sir 信件開頭對收信人的尊稱",
  //       example: [
  //         "How would you like your steak, sir?",
  //         "How would you like your steak, sir?"
  //       ],
  //       exampleSound: "../../media/901833c3347a8bcd/audio/L1pl0_sir.mp3",
  //       exampleTranslation: ["先生，你的牛排要幾分熟呢？","先生，你的牛排要幾分熟呢？"],
  //     },
  //     {//片
  //       idiomContent: ["Dear Sir 信件開頭對收信人的尊稱","Dear Sir 信件開頭對收信人的尊稱"],
  //       example: "How would you like your steak, sir?",
  //       exampleSound: "../../media/901833c3347a8bcd/audio/L1pl0_sir.mp3",
  //       exampleTranslation: "先生，你的牛排要幾分熟呢？",
  //     },
  //     {//用法
  //       usageContent: "Dear Sir 信件開頭對收信人的尊稱",
  //       example: "How would you like your steak, sir?",
  //       exampleSound: "../../media/901833c3347a8bcd/audio/L1pl0_sir.mp3",
  //       exampleTranslation: "先生，你的牛排要幾分熟呢？",
  //     },
  //     {//備註
  //       noteContent: "Dear Sir 信件開頭對收信人的尊稱",
  //       example: "How would you like your steak, sir?",
  //       exampleSound: "../../media/901833c3347a8bcd/audio/L1pl0_sir.mp3",
  //       exampleTranslation: "先生，你的牛排要幾分熟呢？",
  //     },
  //     {//句型
  //       sentenceContent: "Dear Sir 信件開頭對收信人的尊稱",
  //       example: "How would you like your steak, sir?",
  //       exampleSound: "../../media/901833c3347a8bcd/audio/L1pl0_sir.mp3",
  //       exampleTranslation: "先生，你的牛排要幾分熟呢？",
  //     },
  //     {//比較
  //       compareContent: "Dear Sir 信件開頭對收信人的尊稱",
  //       example: "How would you like your steak, sir?",
  //       exampleSound: "../../media/901833c3347a8bcd/audio/L1pl0_sir.mp3",
  //       exampleTranslation: "先生，你的牛排要幾分熟呢？",
  //     },
  //     {//易混淆
  //       confuseContent: "Dear Sir 信件開頭對收信人的尊稱",
  //       example: "How would you like your steak, sir?",
  //       exampleSound: "../../media/901833c3347a8bcd/audio/L1pl0_sir.mp3",
  //       exampleTranslation: "先生，你的牛排要幾分熟呢？",
  //     },
  //     {//句型藍色
  //       sentenceBlueContent: "Dear Sir 信件開頭對收信人的尊稱",
  //       example: "How would you like your steak, sir?",
  //       exampleSound: "../../media/901833c3347a8bcd/audio/L1pl0_sir.mp3",
  //       exampleTranslation: "先生，你的牛排要幾分熟呢？",
  //     }
  //   ]
  // ]
  // console.log(content.plus);

  // 還原縮放（分頁計算須在未縮放的版面上量測，否則高度會失真）
  const resetPanelScale = useCallback(function (panel) {
    if (!panel) return;
    panel.style.transform = '';
    panel.style.transformOrigin = '';
    panel.style.width = '';
    panel.style.height = '';
  }, []);

  // 內容超出畫面時，整頁等比縮小（NBK-1439）
  // 用 transform: scale() 而非調整 font-size：本元件字級多以 vmin / px 宣告（絕對單位），
  // 不繼承容器 font-size，改 font-size 只會讓部分文字縮小。scale 會等比縮放整個子樹。
  // 回傳最後套用的比例（1 = 未縮放），供 panel 外的中文鈕同步縮放
  const fitPanelToContainer = useCallback(function (panel) {
    if (!panel) return 1;

    // 套用比例：同時把 width / height 放大為 1/s，讓內容在放大的版面中重排後再整體縮回，
    // 縮放後剛好填滿外框，右側與下方不會留空隙。
    const applyScale = function (s) {
      if (s >= 1) {
        resetPanelScale(panel);
      } else {
        const pct = (100 / s) + '%';
        panel.style.transformOrigin = 'top left';
        panel.style.width = pct;
        panel.style.height = pct;
        panel.style.transform = 'scale(' + s + ')';
      }
      void panel.offsetHeight;
    };

    const fits = function () {
      return panel.scrollHeight <= panel.clientHeight + 0.5;
    };

    // 先還原再量測，避免沿用上一次的縮放結果
    applyScale(1);
    if (panel.clientHeight <= 0 || fits()) return 1;

    // 二分搜尋「最大且仍塞得下」的比例：s 越小版面越大越容易容納，可容納區間為 [MIN_SCALE, s_max]
    const MIN_SCALE = 0.3;
    let lo = MIN_SCALE;
    let hi = 1;
    let best = null;

    for (let i = 0; i < 12; i++) {
      const mid = (lo + hi) / 2;
      applyScale(mid);
      if (fits()) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }

    const applied = best !== null ? best : MIN_SCALE;
    applyScale(applied);
    return applied;
  }, [resetPanelScale]);

  // 中文鈕與 .tabPanel 同步縮放。
  // 它刻意放在 .vocabularyExample 外：.tabPanel 被套上 transform 後自成 stacking context，
  // 放在裡面的話 z-index 蓋不過畫筆 canvas（z-index 100），畫筆模式下就點不到。
  // 所以不搬進去，而是套用同一個比例，並把縮放原點設在 .tabPanel 的左上角：
  // 幾何上等同於它是 panel 的子元素、跟著內容一起縮放（大小與位置都會跟著變），
  // 但仍留在 panel 的 stacking context 之外，畫筆模式下照樣點得到。
  const translationButtonRef = useRef(null);
  const syncTranslationButtonScale = useCallback(function (panel, scale) {
    const button = translationButtonRef.current;
    if (!button) return;

    button.style.transform = '';
    button.style.transformOrigin = '';
    if (!panel || scale >= 1) return;

    // 兩個 rect 都是視覺座標（含小視窗本身的 popWindowScale 等外層縮放），
    // transform-origin 要的是按鈕自身未縮放的本地座標，須除回外層的累積比例。
    // .tabPanel 以左上角為原點縮放，所以它的 rect 左上角就是未縮放時的位置。
    const buttonRect = button.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const outerScale = button.offsetWidth > 0 ? buttonRect.width / button.offsetWidth : 1;
    const originX = (panelRect.left - buttonRect.left) / outerScale;
    const originY = (panelRect.top - buttonRect.top) / outerScale;
    button.style.transformOrigin = `${originX}px ${originY}px`;
    button.style.transform = `scale(${scale})`;
  }, []);

  // 取得目前頁籤對應的 panel 元素
  const getActivePanelEl = useCallback(function () {
    if (currentTabValue === tabType.vocabulary) return vocabularyRef.current;
    if (currentTabValue === tabType.example) return exampleRef.current;
    if (currentTabValue === tabType.plus || currentTabValue === tabType.extra) return plusRef.current;
    return null;
  }, [currentTabValue]);

  // 頁籤切換／換單字／字級變更／換頁／容器尺寸變化時重新適配
  useEffect(function () {
    const el = getActivePanelEl();
    if (!el) return;

    // 補充頁／一字多義頁須等 contentPages 算完才縮放：
    // 分頁完成前所有 item 都還顯示著，此時縮放會依「全部內容」算出過小的比例，
    // 等分頁完成後才縮放，才會依當前頁實際顯示的那一個 object 計算。
    const isPagedTab = currentTabValue === tabType.plus || currentTabValue === tabType.extra;
    const paginationEnabled = shouldSplited && splitType !== SplitType.NO_SPLIT;
    const waitingForPagination = isPagedTab && paginationEnabled && contentPages.length === 0;

    const run = function () {
      let scale = 1;
      if (waitingForPagination) resetPanelScale(el);
      else scale = fitPanelToContainer(el);
      syncTranslationButtonScale(el, scale);
      // 版面（縮放比例／分頁）定案，通知音檔按鈕的點擊代理重新量測位置。
      // 代理是 absolute 且掛在 pointer-events: none 的圖層上，不會改動 panel 版面，
      // 因此不會回頭觸發下面的 ResizeObserver
      setLayoutVersion(function (v) { return v + 1; });
    };

    let rafId = requestAnimationFrame(run);

    // 只觀察外層容器：panel 自身為 height:100%，內容縮放不會改變其框高，避免觀察自己造成無限迴圈
    const wrapper = el.parentElement;
    let ro = null;
    if (wrapper && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(function () {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(run);
      });
      ro.observe(wrapper);
    }

    return function () {
      cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
    };
  }, [currentTabValue, currentVoca, fontSize, currentPage, contentPages, shouldSplited, splitType, getActivePanelEl, fitPanelToContainer, resetPanelScale, syncTranslationButtonScale]);

  // 切換單字時重置分頁資料。
  // 使用者點單字走 vocaChangeHandler，那裡已同批重置過；這裡負責非 handler 的路徑
  // （初次 mount、defaultWord 帶入的單字），重置成相同值不會影響畫記判斷。
  useEffect(function () {
    setContentPages([]);
    extendedContentDispatch({
      type: types.SET_EXTENDED_CONTENT_CURRENT_PAGE,
      payload: { currentPage: 0 },
    });
  }, [currentVoca, extendedContentDispatch]);

  // ===== DEBUG: 確認補充頁分頁是否被啟用，實測後請整段刪除 =====
  useEffect(function () {
    const isPlusTab = currentTabValue === tabType.plus;
    const isExtraTab = currentTabValue === tabType.extra;
    const items = isPlusTab ? content.plus : isExtraTab ? content.extra : null;
    console.log("[VocabExample][分頁debug]", {
      目前頁籤: currentTabValue,
      目前單字: currentVoca,
      shouldSplited: shouldSplited,
      splitType: splitType,
      是否啟用分頁: !!shouldSplited && splitType !== SplitType.NO_SPLIT,
      item數量: items ? items.length : "(非補充/一字多義頁)",
      contentPages長度: contentPages.length,
      contentPages: contentPages,
      totalPages: totalPages,
      currentPage: currentPage,
      plusRef是否掛載: !!plusRef.current,
    });
  }, [currentTabValue, currentVoca, shouldSplited, splitType, contentPages, totalPages, currentPage, content.plus, content.extra]);
  // ===== DEBUG END =====

  // 補充頁 / 一字多義：陣列裡每個 object 各自成為一頁
  // 不依內容高度併頁，因此完全不需要量測，也就不會受縮放狀態或量測時機影響。
  // 單一 object 內容超出視窗時，由 .plus / .extra 的 overflow-y: auto 捲動處理。
  useEffect(function () {
    const isPlusTab = currentTabValue === tabType.plus;
    const isExtraTab = currentTabValue === tabType.extra;
    if (!isPlusTab && !isExtraTab) return;
    if (!shouldSplited || splitType === SplitType.NO_SPLIT) return;

    if (!plusRef.current) return;
    if (contentPages.length > 0) return;

    const items = isPlusTab ? content.plus : content.extra;
    // 每頁固定一個 object；沒有內容時仍保留一個空頁，避免 totalPages 變成 0
    const pages = items.length > 0
      ? items.map(function (item, index) { return [index]; })
      : [[]];

    setContentPages(pages);
    extendedContentDispatch({
      type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
      payload: { totalPages: pages.length },
    });
  }, [currentTabValue, content.plus, content.extra, contentPages.length, extendedContentDispatch, shouldSplited, splitType]);

  // 按鈕狀態管理
  useEffect(function () {
    let buttonState;
    if (currentTabValue === tabType.vocabulary || currentTabValue === tabType.example) {
      buttonState = ScrollPositionButtonStateType.None;
    } else if (totalPages <= 1) {
      buttonState = ScrollPositionButtonStateType.None;
    } else if (currentPage === 0) {
      buttonState = ScrollPositionButtonStateType.Head;
    } else if (currentPage === totalPages - 1) {
      buttonState = ScrollPositionButtonStateType.Bottom;
    } else {
      buttonState = ScrollPositionButtonStateType.Middle;
    }
    extendedContentDispatch({
      type: types.SET_EXTENDED_CONTENT_SCROLL_POSITION_BUTTON_STATE,
      payload: { scrollPositionButtonState: buttonState },
    });
  }, [currentTabValue, currentPage, totalPages, extendedContentDispatch]);

  // 畫記依「頁籤 × 單字 × 分頁」各自保存。
  // key 由 ExtendedContentEvent 的 getAnnotationGroupKey 組成：
  // extensionType(currentTabValue) + word(currentVoca) 為 group，內層再用 currentSplitedPageIndex(currentPage)。
  const prevTabRef = useRef(currentTabValue);
  const prevVocaRef = useRef(currentVoca);
  const prevPageRef = useRef(currentPage);

  const saveAnnotation = useCallback(function (tabValue, word, pageIndex) {
    EventBus.emit({
      eventBusType: EventBusType.ExtendedContent,
      event: CanvasEvent.SplitedPageSaveCanvasEvent,
      payload: {
        currentSplitedPageIndex: pageIndex,
        extensionType: tabValue,
        word,
      },
    });
  }, []);

  // 切換頁籤／單字／分頁後，載入該組合自己的畫記。
  // 首次 mount 時三個 ref 都等於當前值，不會觸發載入，以保留互動物件既有的畫記。
  useEffect(function () {
    const tabChanged = prevTabRef.current !== currentTabValue;
    const vocaChanged = prevVocaRef.current !== currentVoca;
    const pageChanged = prevPageRef.current !== currentPage;
    if (!tabChanged && !vocaChanged && !pageChanged) return;

    // 頁籤／單字切換的 save 已在 tabChangeHandler / vocaChangeHandler 處理完，
    // 這裡只補「同一頁籤同一單字內翻頁」的 save，避免同一次切換用新 key 存兩遍。
    if (!tabChanged && !vocaChanged && prevPageRef.current >= 0) {
      saveAnnotation(currentTabValue, currentVoca, prevPageRef.current);
    }

    // 延遲一個 tick，確保上面的 save 已寫入後才載入
    const timer = setTimeout(function () {
      // 三個 ref 代表「畫布上目前是哪一組的畫記」，只有在 import 真的執行時才更新。
      // 若在 effect 本體就更新，一次切換中被 cleanup 取消掉的 render（例如切單字時
      // currentVoca 先更新、currentPage 慢一拍才歸零的那個中間狀態）也會把 ref 推進，
      // 使下一次 render 被誤判成「同一單字內翻頁」，把畫布上前一個單字殘留的畫記
      // 存進新單字的舊頁 key —— 症狀是切到新單字後翻頁會冒出上一個單字的畫記。
      prevTabRef.current = currentTabValue;
      prevVocaRef.current = currentVoca;
      prevPageRef.current = currentPage;

      EventBus.emit({
        eventBusType: EventBusType.ExtendedContent,
        event: CanvasEvent.SplitedPageImportJSONEvent,
        payload: {
          currentSplitedPageIndex: currentPage,
          extensionType: currentTabValue,
          word: currentVoca,
        },
      });
    }, 0);
    return function () { clearTimeout(timer); };
  }, [currentTabValue, currentVoca, currentPage, saveAnnotation]);

  const tabLimitedIndex = (index) => {
    const max = tabDataArr.length - 1;
    return Math.max(0, Math.min(index, max));
  };

  const tabChangeHandler = (i) => {
    const index = tabLimitedIndex(i);
    // 切換前先保存目前「頁籤×單字×分頁」的畫記
    saveAnnotation(currentTabValue, currentVoca, currentPage);
    setState({
      currentTabValue: tabDataArr[index].value,
      currentTabIndex: index,
    });
    setContentPages([]);
    extendedContentDispatch({
      type: types.SET_EXTENDED_CONTENT_CURRENT_PAGE,
      payload: { currentPage: 0 },
    });
  };

  const vocaChangeHandler = (word) => {
    hideTW();
    // 切換前先保存目前「頁籤×單字×分頁」的畫記
    saveAnnotation(currentTabValue, currentVoca, currentPage);
    setState({ currentVoca: word });
    // 與 tabChangeHandler 對稱：頁碼重置和 currentVoca 必須在同一批更新裡完成。
    // 交給下方依賴 currentVoca 的 effect 重置會慢一個 render，多出一次
    // 「新單字 × 舊頁碼」的中間狀態（下方 SET_EXTENDED_CONTENT_TOTAL_PAGES 在頁數相同時
    // 會 bail out，不保證幫忙歸零）
    setContentPages([]);
    extendedContentDispatch({
      type: types.SET_EXTENDED_CONTENT_CURRENT_PAGE,
      payload: { currentPage: 0 },
    });
  };

  const vocaPageChangeHandler = (page) => {
    const max = vocaTotalPage;
    setState({ vocaCurrentPage: Math.max(1, Math.min(page, max)) });
  };

  const generatesContentData = useCallback((contents, count) => {
    const data = contents.map((content, index) => ({
      ...content,
      page: Math.floor(index / count) + 1,
    }));
    const pageMap = groupByArrayToMap(data, "page");
    const dataMap = convertArrayToMap(data, "word");
    const totalPage = Math.ceil(contents.length / count);
    return {
      dataMap,
      pageMap,
      totalPage,
    };
  }, []);

  useEffect(() => {
    const { dataMap, pageMap, totalPage } = generatesContentData(
      contents,
      COUNT
    );
    setState({
      vocaDataMap: dataMap,
      vocaPageMap: pageMap,
      vocaTotalPage: totalPage,
      vocaCurrentPage: dataMap[currentVoca].page,
    });
  }, [contents, setState, generatesContentData, currentVoca]);

  useEffect(() => {
    const newTabContent = Object.assign({}, defaultTabContent, {
      plus: content.plus.length > 0 ? defaultTabContent.plus : null,
      extra: content.extra.length > 0 ? defaultTabContent.extra : null,
    });
    const tabDataArr = Object.values(newTabContent).filter((item) => item);
    setState({ tabDataArr });
  }, [currentVoca, content.plus.length, content.extra.length, setState]);

  const isVocaDisabled = useCallback(
    (word) => {
      const { plus, extra } = vocaDataMap[word];
      if (currentTabValue === "plus" && plus.length < 1) return true;
      if (currentTabValue === "extra" && extra.length < 1) return true;
      return false;
    },
    [vocaDataMap, currentTabValue]
  );




  return (
    <>
      {currentTabValue === tabType.example && (
        <TranslationControlsButton
          ref={translationButtonRef}
          className={styles.controlsButton}
          active={isShowTW}
          content="中文"
          onClick={toggleShowTW}
        />
      )}
      <div className={classnames(styles.vocabularyExample, styles[fontSize])}>
        <AudioHitLayer version={layoutVersion}>
        <div className={styles.tabPanelWrapper}>
          {currentTabValue === tabType.vocabulary && (
            <section
              className={classnames(styles.tabPanel, styles.vocabulary)}
              ref={vocabularyRef}
            >
              <div className={styles.left}>
                <div className={styles.word}>{content.word}</div>
                <VocabularyInfo
                  kkContent={content.KK}
                  partOfSpeech={content.partOfSpeech}
                />
                <PortalAudioControlsButton
                  src={ExtendedResourceRepository.getDataSrc({
                    bookId,
                    pathName: content.wordSound,
                  })}
                />
              </div>
              <div className={styles.right}>
                <div
                  className={styles.image}
                  style={{
                    backgroundImage: `url(${ExtendedResourceRepository.getDataSrc(
                      {
                        bookId,
                        pathName: content.image,
                      }
                    )})`,
                  }}
                ></div>
              </div>
            </section>
          )}
          {currentTabValue === tabType.example && (
            <section
              className={classnames(styles.tabPanel, styles.example)}
              ref={exampleRef}
            >
              <div className={styles.title}>
                <div className={styles.info}>
                  <PortalAudioControlsButton
                    className={styles.audioButton}
                    src={ExtendedResourceRepository.getDataSrc({
                      bookId,
                      pathName: content.wordSound,
                    })}
                  />
                  <div className={styles.word}>
                    <div className={styles.vocabularyWord}>
                      <span className={styles.vocaWord}>{content.word}</span>
                    </div>
                    <div className={styles.vocaInfo}>
                      <VocabularyInfo
                        className={styles.vocabularyInfo}
                        kkContent={content.KK}
                        partOfSpeech={content.partOfSpeech}
                      />
                      <div
                        className={classnames(styles.tw, {
                          [styles.active]: isShowTW,
                        })}
                      >
                        {content.wordTranslation}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.content}>
                <div className={styles.exampleSentence}>
                  <PortalAudioControlsButton
                    className={styles.audioButton}
                    src={ExtendedResourceRepository.getDataSrc({
                      bookId,
                      pathName: content.exampleSound,
                    })}
                  />
                  <div className={styles.exampleSentenceContent}>
                    {Array.isArray(content.example) ? (
                      content.example.map((item) => (
                        <p
                          key={item}
                          dangerouslySetInnerHTML={{ __html: item }}
                        />
                      ))
                    ) : (
                      <p
                        dangerouslySetInnerHTML={{ __html: content.example }}
                      />
                    )}
                    {Array.isArray(content.exampleTranslation) ? (
                      content.exampleTranslation.map((item) => (
                        <p
                          className={classnames(styles.tw, {
                            [styles.active]: isShowTW,
                          })}
                        >
                          {item}
                        </p>
                      ))
                    ) : (
                      <p
                        className={classnames(styles.tw, {
                          [styles.active]: isShowTW,
                        })}
                      >
                        {content.exampleTranslation}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}
          {currentTabValue === tabType.plus && (
            <section
              className={classnames(styles.tabPanel, styles.plus)}
              ref={plusRef}
            >
              <div className={styles.content}>
                {content.plus.map(function (item, index) {
                  const isVisible = contentPages.length === 0 ||
                    (contentPages[currentPage] && contentPages[currentPage].indexOf(index) !== -1);
                  return (
                    <div
                      key={index}
                      ref={function (el) { contentItemRefs.current[index] = el; }}
                      style={isVisible ? {} : { display: 'none' }}
                    >
                      {typeof item === "object" ? (
                        <PlusExtraComponent data={item} bookId={bookId} />
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: item }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {currentTabValue === tabType.extra && (
            <section
              className={classnames(styles.tabPanel, styles.extra)}
              ref={plusRef}
              id="extra"
            >
              <div className={styles.content}>
                {content.extra.map(function (item, index) {
                  const isVisible = contentPages.length === 0 ||
                    (contentPages[currentPage] && contentPages[currentPage].indexOf(index) !== -1);
                  return (
                    <div
                      key={index}
                      ref={function (el) { contentItemRefs.current[index] = el; }}
                      style={isVisible ? {} : { display: 'none' }}
                    >
                      {typeof item === "object" ? (
                        <PlusExtraComponent data={item} bookId={bookId} />
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: item }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        <div className={styles.controlBar}>
          {/* 阻止冒泡：ExtendedContentContainer 的 clearCanvas 以 class 名稱（tab/voca/list）
              判斷是否清空畫記，頁籤按鈕的 styles.tab 會命中，導致切換頁籤時畫記被清掉 */}
          <div className={styles.tabSelector} onClick={(e) => e.stopPropagation()}>
            <div className={styles.tabs} ref={tabsRef}>
              {tabDataArr.map(
                (item, index) =>
                  item.value && (
                    <div
                      key={index}
                      className={classnames(styles.tab, {
                        [styles.active]: item.value === currentTabValue,
                      })}
                      onClick={() => tabChangeHandler(index)}
                    >
                      {item.label}
                    </div>
                  )
              )}
            </div>
            <div className={styles.ctrlButtonGroup}>
              <div
                title={tabDataArr[tabLimitedIndex(currentTabIndex - 1)].label}
                className={classnames(styles.ctrl, styles.left, {
                  [styles.disabled]: tabDataArr[0].value === currentTabValue,
                })}
                onClick={() => tabChangeHandler(currentTabIndex - 1)}
              >
                <Icon type="text" name="chevronLeft" />
              </div>
              <div
                title={tabDataArr[tabLimitedIndex(currentTabIndex + 1)].label}
                className={classnames(styles.ctrl, styles.right, {
                  [styles.disabled]:
                    tabDataArr[tabDataArr.length - 1].value === currentTabValue,
                })}
                onClick={() => tabChangeHandler(currentTabIndex + 1)}
              >
                <Icon type="text" name="chevronRight" />
              </div>
            </div>
          </div>
          {/* 同上：單字按鈕的 styles.voca 也會命中 clearCanvas 的比對 */}
          <div className={styles.vocaSelector} onClick={(e) => e.stopPropagation()}>
            <div
              className={classnames(styles.ctrl, styles.left, {
                [styles.hide]: vocaCurrentPage === 1,
              })}
              onClick={() => vocaPageChangeHandler(vocaCurrentPage - 1)}
            >
              <Icon type="text" name="chevronLeft" />
            </div>
            <div className={styles.contentWrapper}>
              {vocaPageMap &&
                vocaPageMap[vocaCurrentPage].map((item) => (
                  <div
                    className={classnames(styles.voca, {
                      [styles.active]: currentVoca === item.word,
                      [styles.disabled]: isVocaDisabled(item.word),
                    })}
                    onClick={() => vocaChangeHandler(item.word)}
                    key={item.word}
                  >
                    {item.word}
                  </div>
                ))}
            </div>
            <div
              className={classnames(styles.ctrl, styles.right, {
                [styles.hide]: vocaCurrentPage === vocaTotalPage,
              })}
              onClick={() => vocaPageChangeHandler(vocaCurrentPage + 1)}
            >
              <Icon type="text" name="chevronRight" />
            </div>
          </div>
        </div>
        </AudioHitLayer>
      </div>
    </>
  );
};

export default VocabularyExample;
